// API Response types
import type { CraClassification } from "../constants/craClassification";

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// User types
export interface User {
  id: string;
  email: string;
  name: string;
  businessArea?: string;
  division?: string;
  membershipStatus?: "requested" | "approved" | "rejected";
  roleId?: string; // Enum ID (global_admin, ba_admin, division_admin, product_owner)
  role?: string; // Display name (Global Admin, Business Area Admin, etc.)
  userType?: "Member" | "Guest";
  isApprover?: boolean;
  supplementaryRoles?: string[];
  createdAt?: string;
}

export const SECURITY_RESEARCHER_ROLE = "security_researcher";

// Roles grantable on top of the primary role, with their display labels.
export const SUPPLEMENTARY_ROLES: { id: string; label: string }[] = [
  { id: SECURITY_RESEARCHER_ROLE, label: "Security Researcher" },
];

export function supplementaryRoleLabel(roleId: string): string {
  return SUPPLEMENTARY_ROLES.find((r) => r.id === roleId)?.label ?? roleId;
}

// Checks both the primary role and supplementary roles. Mirrors backend UserProfile.HasRole.
export function hasRole(user: User | null | undefined, role: string): boolean {
  return (
    user?.roleId === role || (user?.supplementaryRoles ?? []).includes(role)
  );
}

export function canManageSupplementaryRoles(
  user: User | null | undefined,
): boolean {
  return user?.roleId === "global_admin";
}

// Threat model permissions mirror backend services/threat_model_access.go.
export function canCreateThreatModel(user: User | null | undefined): boolean {
  return (
    hasRole(user, "global_admin") || hasRole(user, SECURITY_RESEARCHER_ROLE)
  );
}

export function canEditThreatModel(
  user: User | null | undefined,
  createdById: string | undefined,
): boolean {
  if (hasRole(user, "global_admin")) return true;
  return (
    !!user &&
    !!createdById &&
    createdById === user.id &&
    hasRole(user, SECURITY_RESEARCHER_ROLE)
  );
}

export function canDeleteThreatModel(user: User | null | undefined): boolean {
  return hasRole(user, "global_admin");
}

export function canManageThreatModelPrompts(
  user: User | null | undefined,
): boolean {
  return hasRole(user, "global_admin");
}

// Renaming a family affects every version in it, so only the family creator
// (as a security researcher) or a global admin may do it.
export function canManageThreatModelFamily(
  user: User | null | undefined,
  familyCreatedById: string | undefined,
): boolean {
  return canEditThreatModel(user, familyCreatedById);
}

// Threat model versioning
export type ThreatModelCreationMode = "blank" | "settings" | "full";

export interface ThreatModelFindingsSummary {
  total: number;
  bySeverity: { Critical: number; High: number; Medium: number; Low: number };
  byCategory: Record<string, number>;
}

export interface ThreatModelVersionRef {
  id: string;
  version: string;
}

export interface ThreatModelFamily {
  id: string;
  name: string;
  created_by_id: string;
}

export interface ThreatModelVersion {
  id: string;
  family_id: string;
  version: string;
  version_summary?: string;
  creation_mode: ThreatModelCreationMode;
  archived_at?: string | null;
  name: string;
  description?: string;
  phase: string;
  status: string;
  product_id?: string | null;
  application_id?: string | null;
  findings_summary?: ThreatModelFindingsSummary | null;
  config?: unknown;
  created_by_id?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  family?: ThreatModelFamily;
  source_version?: ThreatModelVersionRef;
  results_inherited?: boolean;
  inherited_from?: ThreatModelVersionRef;
}

export interface ThreatModelFamilyItem extends ThreatModelFamily {
  versions: ThreatModelVersion[];
}

// Utility function to check if user is an admin (global_admin, ba_admin, or division_admin)
export function isAdmin(user: User | null | undefined): boolean {
  return (
    user?.roleId === "global_admin" ||
    user?.roleId === "ba_admin" ||
    user?.roleId === "division_admin"
  );
}

// Whether the user may start/edit/submit assessments for a product:
// an admin (isAdmin) or the product owner. Mirrors backend RequireProductEditor.
export function canManageAssessment(
  user: User | null | undefined,
  ownerId: string | undefined,
): boolean {
  return isAdmin(user) || (!!ownerId && ownerId === user?.id);
}

// Utility function to check if user has global-level access (global_admin or global_reader)
export function hasGlobalAccess(user: User | null | undefined): boolean {
  return user?.roleId === "global_admin" || user?.roleId === "global_reader";
}

// Utility function to check if user has admin dashboard access (global_admin, global_reader, ba_admin, or division_admin)
export function hasAdminDashboard(user: User | null | undefined): boolean {
  return (
    user?.roleId === "global_admin" ||
    user?.roleId === "global_reader" ||
    user?.roleId === "ba_admin" ||
    user?.roleId === "division_admin"
  );
}

// Utility function to check if user can manage other users
export function canManageUsers(user: User | null | undefined): boolean {
  return (
    user?.roleId === "global_admin" ||
    user?.roleId === "ba_admin" ||
    user?.roleId === "division_admin"
  );
}

// Utility function to check if user can view SDL assessment details (global admin, business admin, or division admin)
export function canViewSDLDetails(user: User | null | undefined): boolean {
  return (
    user?.roleId === "global_admin" ||
    user?.roleId === "ba_admin" ||
    user?.roleId === "division_admin"
  );
}

export interface UserCreate {
  email: string;
  name: string;
  businessArea?: string;
  division?: string;
  role?: string;
}

export interface UserUpdate {
  email?: string;
  name?: string;
  businessArea?: string;
  division?: string;
  role?: string;
}

export interface MembershipRequestCreate {
  businessArea: string;
  division: string;
}

export interface DomainInfo {
  domain: string;
  ready: boolean;
}

export interface LicenseRequest {
  id: string;
  requesterEmail: string;
  resource: string;
  requestedTier: number;
  code: boolean;
  chat: boolean;
  design: boolean;
  cowork: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export type FeatureRequestStatus =
  | "submitted"
  | "planned"
  | "in-progress"
  | "blocked"
  | "done"
  | "declined";

// Topics are dynamic (backed by GitHub labels), so any slug is valid.
export type FeatureRequestCategory = string;

export interface FeatureRequest {
  id: string;
  requesterEmail: string;
  requesterName: string;
  title: string;
  description: string;
  category: FeatureRequestCategory;
  githubIssueNumber: number;
  githubIssueUrl: string;
  status: FeatureRequestStatus;
  projectStatus: string;
  etaQuarter: string;
  officialResponse: string;
  officialResponseBy: string;
  officialResponseAt: string | null;
  // Manager-only; omitted from responses for everyone else.
  aiAnalysis?: string;
  aiAnalysisBy?: string;
  aiAnalysisAt?: string | null;
  mergedIntoId: string | null;
  voteCount: number;
  commentCount: number;
  hasVoted: boolean;
  canManage: boolean;
  createdAt: string;
  updatedAt: string;
}

export type OnboardingFieldKind =
  | "text"
  | "email"
  | "textarea"
  | "number"
  | "select"
  | "list"
  | "business-area"
  | "division";

export interface OnboardingFieldOption {
  value: string;
  label: string;
}

export interface OnboardingField {
  key: string;
  label: string;
  kind: OnboardingFieldKind;
  required: boolean;
  description?: string;
  placeholder?: string;
  help?: string;
  pattern?: string;
  maxLength?: number;
  minimum?: number;
  default?: string;
  options?: OnboardingFieldOption[];
  prefill?: "name" | "email" | "businessArea" | "division";
  availabilityCheck?: "namespace";
}

export interface OnboardingFieldGroup {
  title: string;
  description?: string;
  fields: OnboardingField[];
}

export interface OnboardingRequestType {
  slug: string;
  label: string;
  description: string;
  groups: OnboardingFieldGroup[];
}

export type OnboardingFormValues = Record<string, string | string[]>;

export interface OnboardingRequest {
  id: string;
  type: string;
  requesterId: string;
  requesterEmail: string;
  requesterName: string;
  title: string;
  fields: Record<string, unknown>;
  status: FeatureRequestStatus;
  statusChangedAt: string | null;
  statusChangedBy: string;
  statusChangedByName: string;
  editedAt: string | null;
  editedBy: string;
  editedByName: string;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureRequestComment {
  id: string;
  featureRequestId: string;
  authorEmail: string;
  authorName: string;
  body: string;
  isOfficial: boolean;
  githubCommentId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoadmapTeam {
  id: string;
  name: string;
  slug: string;
  createdByEmail: string;
  members: string[];
  canEdit: boolean;
  canManageTopics: boolean;
}

export interface RoadmapTeamCreate {
  name: string;
}

export interface RoadmapTopic {
  id: string;
  teamId: string;
  name: string;
  slug: string;
  canEdit: boolean;
  canAssign: boolean;
}

export interface RoadmapTopicCreate {
  teamId: string;
  name: string;
}

export interface RoadmapTopicUpdate {
  name?: string;
  teamId?: string;
}

export type RoadmapItemStatus =
  | "backlog"
  | "in_progress"
  | "review"
  | "blocked"
  | "done";

export interface RoadmapItem {
  acceptanceCriteria?: RoadmapAcceptanceCriterion[];
  dependencies?: string;
  status: RoadmapItemStatus;
  id: string;
  createdByName?: string;
  createdByEmail: string;
  createdAt: string;
  topicId: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
}

export interface RoadmapItemCreate {
  acceptanceCriteria?: RoadmapAcceptanceCriterion[];
  dependencies?: string;
  status?: RoadmapItemStatus;
  topicId: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
}

export interface RoadmapAcceptanceCriterion {
  id: string;
  text: string;
  completed: boolean;
}

export type RoadmapItemUpdate = Partial<RoadmapItemCreate> & {
  criterion?: { id: string; completed: boolean };
};

export interface RoadmapVotes {
  upvotes: number;
  downvotes: number;
  myVote: -1 | 0 | 1;
}

export interface RoadmapComment {
  id: string;
  itemId: string;
  authorEmail: string;
  authorName: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export function canEditRoadmapComment(
  user: User | null | undefined,
  comment: RoadmapComment,
): boolean {
  return Boolean(
    user?.email &&
      user.email.trim().toLowerCase() === comment.authorEmail.toLowerCase(),
  );
}

export function canDeleteRoadmapComment(
  user: User | null | undefined,
  comment: RoadmapComment,
  teamOwnerEmail: string,
): boolean {
  return (
    user?.roleId === "global_admin" ||
    canEditRoadmapComment(user, comment) ||
    Boolean(
      user?.email &&
        user.email.trim().toLowerCase() === teamOwnerEmail.trim().toLowerCase(),
    )
  );
}

export interface LicenseInboxItem {
  id: string;
  requesterEmail: string;
  resource: string;
  requestedTier: number;
  code: boolean;
  chat: boolean;
  design: boolean;
  cowork: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  domain: string;
}

export interface SecurityAdvisory {
  id: string;
  productId?: string;
  title: string;
  businessArea: string;
  division: string;
  status: "draft" | "final";
  version: number;
  brandKey: string;
  csaf: Record<string, unknown>;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityAdvisoryListResponse {
  items: SecurityAdvisory[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Role types
export interface Role {
  id: string; // Enum ID (global_admin, ba_admin, division_admin, product_owner)
  name: string; // Display name (Global Admin, Business Area Admin, etc.)
  description: string;
}

// Auth response type (matches backend)
export interface AuthResponse {
  user: User;
  token: string;
}

// Product types
export interface Product {
  id: string;
  name: string;
  businessArea: string;
  division: string;
  description?: string;
  category?: string;
  status: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  lastChangedAt: string;
  lastChangedBy: string;
  craApplicability?: string;
  sdlMaturityScore?: number;
  sdlMaturityPeriod?: string;
  sdlPracticeScores?: { [practice: string]: number };
  sdlLastFillingInDate?: string;
  // New fields
  apmNumber?: string;
  productResponsibleId?: string;
  businessAccountableId?: string;
  sdlResponsibleId?: string;
  developmentResponsibility?: "Sandvik" | "Supplier" | "Sub-Contractor";
  customization?: "Customized" | "COTS";
  lifecycleStatus?:
    | "Planned"
    | "In Development"
    | "Released"
    | "Retired"
    | "Obsolete";
  craClassification?: CraClassification;
  keyProduct?: boolean;
  dataActStatus?: string;
  craComplianceStatus?: string;
  craComplianceAnsweredCount?: number;
  owner: User;
  lastChangedByUser?: User;
  // New user relationships
  productResponsible?: User;
  businessAccountable?: User;
  sdlResponsible?: User;
  productTags?: ProductTag[];
  productRelationships?: ProductRelationshipWithProducts[];
  applications?: Application[];
  _count: {
    craAssessments: number;
    sdlAssessments: number;
  };
}

export interface ProductCreate {
  name: string;
  businessArea: string;
  division: string;
  description?: string;
  category?: string;
  // New fields
  apmNumber?: string;
  productResponsibleId?: string;
  businessAccountableId?: string;
  sdlResponsibleId?: string;
  developmentResponsibility?: "Sandvik" | "Supplier" | "Sub-Contractor";
  customization?: "Customized" | "COTS";
  lifecycleStatus?:
    | "Planned"
    | "In Development"
    | "Released"
    | "Retired"
    | "Obsolete";
  craClassification?: CraClassification;
  keyProduct?: boolean;
  tags?: ProductTagInput[] | undefined;
  relationships?: RelationshipInput[] | undefined;
}

export interface ProductUpdate extends Partial<ProductCreate> {}

// CRA Economic Operator (per-product legal entity tied to a CRA role).
// Each entry is bound to exactly one role; a product may have multiple entries.
export type CRAEconomicOperatorRole =
  | "manufacturer"
  | "importer"
  | "distributor";

export interface CRAEconomicOperator {
  id: string;
  productId: string;
  legalEntityName: string;
  corporateId: string;
  role: CRAEconomicOperatorRole;
  mainEstablishment?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CRAEconomicOperatorInput {
  legalEntityName: string;
  corporateId: string;
  role: CRAEconomicOperatorRole;
  mainEstablishment?: string | null;
}

export interface ProductListResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
}

// Tag types
export interface TagDefinitionValue {
  value: string;
  label: string;
  description: string;
}

export interface TagDefinitionKey {
  id: string;
  label: string;
  description: string;
  multiValue: boolean;
  values: TagDefinitionValue[];
}

export interface TagDefinitionCategory {
  id: string;
  label: string;
  description: string;
  keys: TagDefinitionKey[];
}

export interface ProductTag {
  id: string;
  category: string;
  key: string;
  value: string;
}

export interface ProductTagInput {
  key: string;
  value: string;
}

// Relationship types
export interface RelationshipTypeDefinition {
  id: string;
  label: string;
  description: string;
  /**
   * When set, this relationship type is asymmetric. `label` names the
   * outgoing/source perspective (e.g. "Parent of") and `inverseLabel` names
   * the incoming/target perspective (e.g. "Child of"). When omitted, the type
   * is symmetric and both ends use `label`.
   */
  inverseLabel?: string;
}

export interface RelationshipInput {
  targetProductId: string;
  type: string;
  criticality?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  notes?: string | undefined;
}

export interface ProductRelationshipWithProducts {
  id: string;
  sourceId: string;
  targetId: string;
  type: string;
  typeLabel: string;
  criticality?: string;
  since?: string;
  until?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  sourceProduct: { id: string; name: string };
  targetProduct: { id: string; name: string };
}

// Assessment types
export interface CRAQuestion {
  id: number;
  question: string;
  description: string;
  yesRedirect: number | "end";
  noRedirect: number | "end";
}

export interface CRAAnswer {
  questionId: number;
  answer: boolean;
  comment?: string;
}

export interface CRAAssessment {
  id: string;
  productId: string;
  userId: string;
  version: string;
  answers: CRAAnswer[];
  result?: string;
  resultReason?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  user: User;
  product: Product;
}

export interface SDLQuestion {
  id: number;
  practice: string;
  name: string;
  description: string;
  criteria?: string;
}

export interface SDLAnswer {
  questionId: number;
  maturityScore: number | null;
  details: string;
  evidence: string;
}

// SDL Assessment Period types
export interface SDLPeriod {
  id: string;
  year: number;
  quarter: number;
  isOpen: boolean;
  isCurrent: boolean;
}

export interface SDLPeriodCreate {
  year: number;
  quarter: number;
  isOpen?: boolean;
}

export interface SDLAssessmentWithStatus extends SDLAssessment {
  status: "draft" | "submitted";
}

export interface SDLAssessmentPeriodForProduct {
  id: string | null;
  year: number;
  quarter: number;
  period: string;
  isOpen: boolean;
  isCurrent: boolean;
  hasAssessment: boolean;
  assessmentStatus: "draft" | "submitted" | null;
  assessmentId?: string | null;
  answeredCount?: number | null;
  totalQuestions?: number | null;
  lastSavedAt?: string | null;
  maturityScore?: number | null;
}

export interface SDLAssessmentParticipant {
  id: string;
  assessmentId: string;
  /** Null once the underlying account has been deleted. */
  userId: string | null;
  /** Snapshot taken when the assessment was recorded. */
  userName: string;
  userEmail: string;
  createdAt: string;
}

export interface SDLAssessment {
  id: string;
  productId: string;
  userId: string;
  version: string;
  period: string;
  answers: SDLAnswer[];
  practiceScores: Record<string, number>;
  overallScore: number;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  user: User;
  product: Product;
  participants?: SDLAssessmentParticipant[];
}

// Ownership request types
export interface OwnershipRequest {
  id: string;
  productId: string;
  requestedById: string;
  currentOwnerId: string;
  status: "pending" | "approved" | "denied";
  requestedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  product: Product;
  requestedBy: User;
  currentOwner: User;
}

// Repository types
export interface CustomProperty {
  repositoryId: string;
  propertyName: string;
  value: string;
}

export interface RepoContributor {
  repositoryId: string;
  login: string;
  avatarUrl: string;
  email: string;
  commitDate: string;
}

export interface Repository {
  id: string;
  fullName: string;
  name: string;
  owner: string;
  description?: string;
  visibility: string;
  defaultBranch: string;
  archived: boolean;
  disabled: boolean;
  fork: boolean;
  language?: string;
  forksCount: number;
  starsCount: number;
  openIssues: number;
  pushedAt?: string;
  htmlUrl: string;
  createdAt: string;
  updatedAt: string;
  recentContributorCount?: number;
  lastCommitAt?: string;
  businessArea?: string;
  businessAreaName?: string;
  businessUnit?: string;
  businessUnitDescription?: string;
  companyId?: string;
  companyName?: string;
  productArea?: string;
  productAreaName?: string;
  positionCostCenterId?: string;
  customProperties?: CustomProperty[];
  contributors?: RepoContributor[];
}

export interface RepositoryListResponse {
  repositories: Repository[];
  total: number;
  page: number;
  limit: number;
}

export interface RepositoryFilters {
  search?: string;
  owner?: string;
  language?: string;
  archived?: boolean;
  page?: number;
  limit?: number;
}

export interface SingleValueData {
  value: number;
  label?: string;
}

export interface PieChartItem {
  name: string;
  value: number;
}

export interface PieChartData {
  items: PieChartItem[];
}

export interface BarChartItem {
  label: string;
  value: number;
}

export interface BarChartData {
  items: BarChartItem[];
}

export interface MultiBarChartItem {
  label: string;
  values: Record<string, number>;
}

export interface MultiBarChartData {
  series: string[];
  items: MultiBarChartItem[];
}

export interface FilterOptionsData {
  businessAreas: string[];
  companies: string[];
  productAreas: string[];
  companiesByBusinessArea: Record<string, string[]>;
  productAreasByBusinessArea: Record<string, string[]>;
  productAreasByCompany: Record<string, string[]>;
}

export interface CopilotWidgetFilters {
  businessAreaName?: string;
  companyName?: string;
  productAreaName?: string;
}

// Application types
export interface Application {
  id: string;
  name: string;
  businessArea: string;
  division: string;
  description?: string;
  applicationType?: string;
  environment?: string;
  deploymentUrl?: string;
  status: string;
  ownerId: string;
  lastChangedBy: string;
  owner: User;
  products?: Product[];
  repositories?: Repository[];
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationCreate {
  name: string;
  businessArea: string;
  division: string;
  description?: string;
  applicationType?: string;
  environment?: string;
  deploymentUrl?: string;
  status?: string;
  ownerId?: string;
}

export interface ApplicationUpdate {
  name?: string;
  businessArea?: string;
  division?: string;
  description?: string;
  applicationType?: string;
  environment?: string;
  deploymentUrl?: string;
  status?: string;
  ownerId?: string;
}

export interface ApplicationListResponse {
  applications: Application[];
  total: number;
  page: number;
  limit: number;
}

// A manual link between an application and a Dependency-Track project.
export interface ApplicationDependencyTrackLink {
  id: string;
  applicationId: string;
  dtProjectUuid: string;
  dtProjectName?: string;
  dtProjectVersion?: string;
  linkedById?: string;
  linkedByName?: string;
  createdAt: string;
  updatedAt: string;
}

// A single vulnerability finding (e.g. a CVE affecting a component). The
// source-specific fields (projectUuid, projectName) identify the upstream
// project that reported it; applicationId/applicationName trace it back to the
// prod-atlas application when viewed at the product level.
export interface VulnerabilityFinding {
  vulnId: string;
  source: string;
  severity: string;
  cvssScore: number;
  componentName: string;
  componentVersion: string;
  componentPurl: string;
  title: string;
  description: string;
  suppressed: boolean;
  projectUuid: string;
  projectName: string;
  projectVersion?: string;
  applicationId?: string;
  applicationName?: string;
}

// One linked project whose findings could not be loaded.
export interface ProjectLoadError {
  dtProjectUuid: string;
  dtProjectName: string;
  error: string;
}

// One page of an application's aggregated live findings across linked projects.
// `findings` is the current page; the severity counts and `total` cover every
// page.
export interface ApplicationFindingsResult {
  findings: VulnerabilityFinding[];
  errors: ProjectLoadError[];
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  unassigned: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// One page of a product's aggregated live findings across all linked applications.
export interface ProductFindingsResult {
  findings: VulnerabilityFinding[];
  errors: ProjectLoadError[];
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  unassigned: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// One version of a linked Dependency-Track project.
export interface DtProjectVersion {
  uuid: string;
  version: string;
  isLatest: boolean;
  lastBomImport: number;
}

// A linked Dependency-Track project with its selectable versions, newest first.
export interface LinkedDtProject {
  linkId: string;
  applicationId: string;
  applicationName?: string;
  projectUuid: string;
  projectName: string;
  latestUuid: string;
  versions: DtProjectVersion[];
}

// Per-link version choice: "all" or a version UUID. Absent links show latest.
export type DtVersionSelection = Record<string, string>;

export interface ApplicationFilters {
  search?: string;
  businessArea?: string;
  division?: string;
  status?: string;
  applicationType?: string;
  productId?: string;
  page?: number;
  limit?: number;
}

// Filter types
export interface ProductFilters {
  businessArea?: string;
  division?: string;
  search?: string;
  page?: number;
  limit?: number;
  craStatus?: string; // "done" or "not_done"
  sdlStatus?: string; // "done" or "not_done"
  status?: string; // "Approved" or "NotApproved"
  keyProduct?: string; // "yes" or "no"
  aiFeatures?: string; // "yes" or "no" -- matches the ai-features product tag
  craComplianceStatus?: string; // "submitted", "draft", or "not_started"
  dataActStatus?: string; // "in_scope", "out_of_scope", "draft", or "not_assessed"
  lifecycleStatus?: string; // "Planned", "In Development", "Released", "Retired", "Obsolete"
}

// Assessment status
// Who last changed an assessment, and when. Absent when the assessment does
// not exist, or when it predates the columns that record this.
export interface AssessmentAudit {
  /** RFC3339, UTC. Render in the viewer's local zone. */
  lastModifiedAt: string;
  /** Resolved display name. Empty when the user account no longer exists. */
  lastModifiedBy?: string;
}

export interface AssessmentStatus {
  cra: {
    completed: boolean;
    result?: string;
    lastCompleted?: string;
  };
  sdl: {
    completed: boolean;
    score?: number;
    period?: string;
    lastCompleted?: string;
  };
  sdlMaturityScore?: number;
  sdlAssessmentDate?: string;
  dataActStatus?: string;

  craApplicabilityAudit?: AssessmentAudit;
  craComplianceAudit?: AssessmentAudit;
  dataActAudit?: AssessmentAudit;
  /** Only populated for the admin roles that may see SDL detail. */
  sdlAudit?: AssessmentAudit;
}

// Division statistics for division admins
export interface DivisionStatistics {
  totalProducts: number;
  approvedProducts: number;
  pendingProducts: number;
  productsWithCRA: number;
  productsWithSDL: number;
  averageSDLScore: number;
  craCompletionRate: number;
  sdlCompletionRate: number;
  approvalRate: number;
  productsByCategory: Record<string, number>;
}

// Business area statistics for BA admins
export interface BusinessAreaStatistics {
  totalProducts: number;
  approvedProducts: number;
  pendingProducts: number;
  productsWithCRA: number;
  productsWithSDL: number;
  averageSDLScore: number;
  craCompletionRate: number;
  sdlCompletionRate: number;
  approvalRate: number;
  productsByCategory: Record<string, number>;
  productsByDivision: Record<string, number>;
}

// Data Act Assessment types
export interface DataActOption {
  value: string;
  label: string;
  description?: string;
}

export interface DataActQuestionLabel {
  product?: string;
  service?: string;
  // Fixed label used by single-path questionnaires such as the CRA
  // applicability assessment, which have no product/service branching.
  cra?: string;
}

export interface DataActQuestion {
  id: string;
  title: string;
  description?: string;
  type: "single_choice" | "three_way_choice" | "text";
  required: boolean;
  options?: DataActOption[];
  guidance?: string;
  guidanceImages?: string[];
  prefill?: string;
  infoOnly?: boolean;
  hidden?: boolean;
  visibleWhen?: Record<string, unknown>;
  questionLabel?: DataActQuestionLabel;
}

export interface DataActSection {
  id: string;
  title: string;
  sectionLabel?: DataActQuestionLabel;
  description?: string;
  visibleWhen: Record<string, unknown> | null;
  questions: DataActQuestion[];
}

export interface DataActQuestionsJSON {
  version: string;
  sections: DataActSection[];
}

export interface DataActAnswer {
  questionId: string;
  answer: string;
  details: string;
}

export interface DataActAssessment {
  id: string;
  productId: string;
  userId: string;
  version: number;
  status: "draft" | "submitted";
  answers: string;
  result?: string;
  currentPage: number;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DataActAssessmentCreate {
  answers: DataActAnswer[];
  version: string;
  status?: string;
  currentPage?: number;
}

export interface DataActResult {
  inScope: boolean;
  outOfScopeReason?: string;
  classifications: {
    connectedProduct: boolean;
    relatedService: boolean;
    dataProcessingService: boolean;
  };
  roles: {
    manufacturer: boolean;
    // Absent in results submitted before the field was added
    dataHolder?: boolean;
    relatedServiceProvider: boolean;
    user: boolean;
  };
  dataCategories: {
    productData: boolean;
    relatedServiceData: boolean;
    enrichmentLevel?: string;
  };
  properties: {
    readilyAvailable: boolean;
    tradeSecrets: boolean;
    safetyRisk: boolean;
    personalData: boolean;
  };
}

export interface DataActLinkedCandidate {
  id: string;
  name: string;
  productType: string; // "hardware" | "software" | ""
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  latestAssessmentId?: string;
  latestAssessmentStatus?: string; // "submitted" | ""
  connectedProduct: boolean;
  relatedService: boolean;
  dataProcessingService: boolean;
}

/**
 * Stored as JSON in `DataActAnswer.details` for Q2.5 and Q2.11 to record
 * whether the user picked linked peers or entered details manually. The rest
 * of the picker-driven questions (names, owner, assessment-completed) are
 * written as plain answers so result computation remains unchanged.
 */
export interface DataActLinkedAnswerDetails {
  mode: "linked" | "manual";
  peerProductIds?: string[];
}

// Global statistics for global admins/readers
export interface GlobalStatistics {
  totalProducts: number;
  approvedProducts: number;
  pendingProducts: number;
  productsWithCRA: number;
  productsWithSDL: number;
  averageSDLScore: number;
  craCompletionRate: number;
  sdlCompletionRate: number;
  approvalRate: number;
  productsByCategory: Record<string, number>;
  productsByBusinessArea: Record<string, number>;
  productsByDivision: Record<string, number>;
}

// Notification settings
export interface NotificationSettings {
  id: string;
  userId: string;
  overdueAssessmentsEnabled: boolean;
  cadenceDays: number;
  lastNotifiedAt?: string;
}

// CRA Compliance Survey types
export interface RequirementLibraryEntry {
  id: number;
  title: string;
  craReference?: string;
  description?: string;
}

export interface CRAComplianceSubQuestion {
  ref: string;
  displayNumber?: string;
  name?: string;
  question: string;
  answerOptions: string[];
  expectedEvidence: string;
  requirementLibraryIds?: number[];
  requirementLibrary?: RequirementLibraryEntry[];
}

export interface CRAComplianceQuestion {
  ref: string;
  displayNumber?: string;
  name: string;
  question: string;
  subQuestions: CRAComplianceSubQuestion[];
  answerOptions: string[];
  expectedEvidence: string;
  requirementLibraryIds?: number[];
  requirementLibrary?: RequirementLibraryEntry[];
}

export interface CRAComplianceAnswer {
  questionRef: string;
  answer: string;
  details: string;
}

export interface CRAComplianceAssessment {
  id: string;
  productId: string;
  userId: string;
  version: string;
  status: "draft" | "submitted";
  answers: CRAComplianceAnswer[] | string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// DT API Key types
export interface DtAPIKeyPrefix {
  id: string;
  apiKeyId: string;
  allowedPrefix: string;
  createdAt: string;
}

export interface DtAPIKeyOwner {
  id: string;
  apiKeyId: string;
  userId: string;
  user: User;
}

export interface DtAPIKey {
  id: string;
  owner: string;
  isActive: boolean;
  createdBy?: string;
  rawKey?: string;
  prefixes: DtAPIKeyPrefix[];
  owners: DtAPIKeyOwner[];
  createdAt: string;
  updatedAt: string;
}

export interface DtAPIKeyCreate {
  owner: string;
  prefixes: string[];
  ownerIds: string[];
}

export interface DtAPIKeyUpdate {
  owner?: string;
  isActive?: boolean;
  prefixes?: string[];
  ownerIds?: string[];
}

export interface DtAPIKeyCreateResponse {
  apiKey: DtAPIKey;
}

export interface DtAPIKeyListResponse {
  items: DtAPIKey[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
